import Image from 'next/image';
import NextLink from 'next/link';

import React, { useEffect } from 'react';

import styled from 'styled-components';

import { Badge } from '@components/common/Badge';
import { Features } from '@components/common/Features';
import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';
import { Link } from '@components/parts/Link';
import { MarqueeCards } from '@components/parts/MarqueeCards';

import { DynamicData } from '@customTypes/DynamicData';
import { LinkProps } from '@customTypes/Link';
import { UploadedImage } from '@customTypes/UploadedImage';

type Partner = {
  partnerId?: number;
  name?: string;
  profile?: UploadedImage;
  star?: number;
  ratings?: number;
  features?: string;
  verified?: boolean;
};

type Props = {
  backgroundColor?: string;
  useDynamicValue: boolean;
  dynamicData?: DynamicData;
  partners: Partner[];
  action?: LinkProps;
  partnerVerifiedFeatures?: string;
  partnerUnverifiedFeatures?: string;
};

const Wrapper = styled.div`
  padding-top: 50px;
`;

const Card = styled.div`
  margin-left: 20px;
  width: 320px;
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 1px solid #e3e5ea;
  padding: 24px;
  height: 220px;
  @media (min-width: 992px) {
    width: 350px;
    height: 240px;
  }
  .profile {
    display: flex;
    flex-direction: row;
    gap: 16px;

    .left {
      position: relative;

      img {
        border-radius: 30px;
      }
      &.verified::after {
        content: '';
        display: block;
        position: absolute;
        right: -8px;
        bottom: 0px;
        width: 26px;
        height: 26px;
        background-image: url('/svg/verified.svg');
      }
    }
    .right {
      display: flex;
      flex-direction: column;
      justify-content: center;
      .name {
        font-size: 1.2em;
        font-weight: 700;
      }
      .description {
        .star-rating {
          margin-left: 0.1em;
          font-size: 1em;
          font-weight: 700;
        }
        .ratings {
          margin-left: 0.1em;
          font-size: 1em;
          color: #62656a;
        }
        display: flex;
        flex-direction: row;
        align-items: center;
      }
    }
  }
`;

function convertDynamic(
  dynamicData: DynamicData,
  partnerVerifiedFeatures?: string,
  partnerUnverifiedFeatures?: string
): Partner[] {
  return (
    dynamicData.partners?.map((partner) => ({
      partnerId: partner.id,
      name: partner.name,
      ratings: partner.review_count,
      star: partner.score,
      verified: partner.verified,
      profile: {
        data: {
          attributes: {
            id: Math.random(),
            url: partner.profile,
          },
        },
      },
      features: (partner.verified ? partnerVerifiedFeatures : partnerUnverifiedFeatures) ?? '',
    })) ?? []
  );
}

export const PartnerBanners: React.FC<Props> = (props) => {
  const {
    backgroundColor,
    useDynamicValue,
    dynamicData,
    partners: $partners,
    action,
    partnerVerifiedFeatures: $partnerVerifiedFeatures,
    partnerUnverifiedFeatures: $partnerUnverifiedFeatures,
  } = props;
  const partnerVerifiedFeatures = dynamicData?.partnerVerifiedFeatures || $partnerVerifiedFeatures;
  const partnerUnverifiedFeatures = dynamicData?.partnerUnverifiedFeatures || $partnerUnverifiedFeatures;
  const partners =
    useDynamicValue && dynamicData
      ? convertDynamic(dynamicData, partnerVerifiedFeatures, partnerUnverifiedFeatures)
      : $partners;
  useEffect(() => {
    const doc = document.documentElement;
    doc.style.setProperty('--display-partners', (partners?.length ?? 0) < 4 ? 'none' : 'block');
  }, [partners?.length]);
  const partnerUnverified = !partners?.every((p) => p.verified);
  const defaultBackground = partnerUnverified ? '#ffffff' : '#172030';
  const partnerLink = (partner: Partner) => {
    if (!partner.partnerId || !dynamicData?.id) return { href: '#' };
    return { href: `/partners/${partner.partnerId}?serviceId=${dynamicData.id}` };
  };

  const isValidPartner = (partner: Partner) => partner.name !== '000';

  return (
    <Wrapper className="hide-if-scarce-partners" style={{ backgroundColor: backgroundColor || defaultBackground }}>
      <ScrollReveal>
        <MarqueeCards duration={(partners.length + 1) * 6}>
          {partners.filter(isValidPartner).map((partner, index) => (
            <NextLink key={`${index}`} {...partnerLink(partner)}>
              <Card>
                <div className="profile">
                  <div className={`left ${partner.verified ? 'verified' : ''}`}>
                    <Image
                      src={partner.profile?.data?.attributes.url || '/svg/partner_profile_default.svg'}
                      alt={partner.name || 'Partner'}
                      width={60}
                      height={60}
                    />
                  </div>
                  <div className="right">
                    <div className="name">{partner.name}</div>
                    <div className="description">
                      {partner.verified ? <Badge>인증 파트너</Badge> : null}
                      <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8.03906 1.09673C8.21069 0.688368 8.78931 0.688367 8.96094 1.09673L10.746 5.34402C10.8185 5.51639 10.981 5.63393 11.1674 5.64872L15.7914 6.01557C16.2362 6.05086 16.4156 6.60688 16.0752 6.89541L12.5599 9.87533C12.4159 9.99745 12.3529 10.1903 12.3972 10.3739L13.4725 14.835C13.5766 15.2669 13.1079 15.6099 12.7278 15.3801L8.75865 12.981C8.59961 12.8849 8.40039 12.8849 8.24135 12.981L4.27224 15.3801C3.89208 15.6099 3.42342 15.2669 3.52751 14.8351L4.60283 10.3739C4.64708 10.1903 4.58412 9.99745 4.44007 9.87533L0.92479 6.89541C0.584434 6.60688 0.763768 6.05086 1.20856 6.01557L5.83257 5.64872C6.01896 5.63393 6.18152 5.51639 6.25397 5.34402L8.03906 1.09673Z"
                          fill="#FFBF51"
                        />
                      </svg>
                      <div className="star-rating">{partner.star?.toFixed(1)}</div>
                      <div className="ratings">({partner.ratings})</div>
                    </div>
                  </div>
                </div>
                <Features features={partner.features?.split('\n').filter((a) => a) ?? []} />
              </Card>
            </NextLink>
          ))}
        </MarqueeCards>
      </ScrollReveal>

      {action ? (
        <ScrollReveal>
          <Container>
            <Link {...action} className={`${action.className} mt-40`} />
          </Container>
        </ScrollReveal>
      ) : null}
    </Wrapper>
  );
};
